import React from 'react'
import { useLuminaireActiveEnergy } from '../../hooks/useLuminaireMetrics'
import { LuminaireMetricFilter } from '../../types/luminaire.types'
import { createList } from '../../../../common/utils/graph.utils'
import { formatDateByPeriod, getPeriodLabel } from '../../../../common/utils/dateFormatter'
import {
  EChartsPresenter,
  EChartsData,
  EChartsSeries,
} from '../../../../common/components/molecule/EChartsView'
import ProfessionalChartLoader from '../../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { AggregateWindow } from '../../../filter/types/types'

interface Props {
  devEui: string
  filter: LuminaireMetricFilter
}

export default function LuminaireConsumptionChartPresenter({ devEui, filter }: Props) {
  const { activeEnergyQuery } = useLuminaireActiveEnergy(devEui, filter)

  const aggWindow = (filter.aggregateWindow || '1h') as AggregateWindow
  const periodLabel = getPeriodLabel(aggWindow)
  const baseTitle = `Évolution de la Consommation ${periodLabel} d'Énergie`

  if (activeEnergyQuery.isLoading) {
    return <ProfessionalChartLoader metricName="Index et Consommation" />
  }

  if (activeEnergyQuery.isError) {
    return <ChartErrorView metricName="Index et Consommation" />
  }

  if (!activeEnergyQuery.data || activeEnergyQuery.data.length === 0) {
    return <ChartErrorView metricName="Index et Consommation" />
  }

  const filterPoint = {
    startDate: filter.startDate,
    stopDate: filter.stopDate,
    aggregateWindow: aggWindow,
  }
  const dataChart = createList(aggWindow, filterPoint, activeEnergyQuery.data)

  const indexValues = dataChart.listValue
  const consumptionValues: (number | null)[] = indexValues.map((value, idx) => {
    if (idx === 0 || value === null || value === undefined) return null
    const prev = indexValues[idx - 1]
    if (prev === null || prev === undefined) return null
    const delta = (value as number) - (prev as number)
    return delta >= 0 ? delta : null
  })

  const indexData: EChartsData[] = dataChart.listx.map((date, idx) => ({
    name: formatDateByPeriod(date, aggWindow),
    value: indexValues[idx] ?? null,
    color: '#3b82f6',
  }))

  const consumptionData: EChartsData[] = dataChart.listx.map((date, idx) => ({
    name: formatDateByPeriod(date, aggWindow),
    value: consumptionValues[idx],
    color: '#10b981',
  }))

  const combinedSeries: EChartsSeries[] = [
    {
      name: 'Index de Consommation (kWh)',
      data: indexData,
      type: 'line',
      color: '#3b82f6',
    },
    {
      name: "Consommation d'Énergie (kWh)",
      data: consumptionData,
      type: 'bar',
      color: '#10b981',
    },
  ]

  return (
    <EChartsPresenter
      data={combinedSeries}
      title={baseTitle}
      type="combined"
      height={400}
      xAxisLabel="Date"
      yAxisLabel="Consommation (kWh)"
      showLegend={true}
      showTooltip={true}
    />
  )
}
